import { useTranslation } from 'react-i18next';

import type { BookingEventDetail, BookingEventRegistration } from '@/types';

import { DetailField } from '@/components';
import { Form } from '@/components/Form';

import { useAppForm } from '@/hooks';

import { bookingDateTimeRange } from '@/helpers';

import { createBookingEventRegistrationMoveSchema } from '@/types/schemas';

import { BookingDialogBody } from '../BookingDialogBody';
import { BookingDialogFooter } from '../BookingDialogFooter';
import { BookingPersonName } from '../BookingPersonName';

import { MoveWaveField } from './MoveWaveField';
import {
  buildRegistrationsUrl,
  moveEventRegistrationPayload,
  moveOptions,
} from './utils';

type MoveRegistrationFormProps = {
  event: BookingEventDetail;
  registration: BookingEventRegistration;
  eventsUrl: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const MoveRegistrationForm = (props: MoveRegistrationFormProps) => {
  const {
    event,
    registration,
    eventsUrl,
    onClose,
    onSuccess: propOnSuccess,
  } = props;
  const { t } = useTranslation();

  const currentWave = event.waves.find(
    (wave) => wave.id === registration.event_wave_id,
  );

  const onSuccess = () => {
    propOnSuccess();
    onClose();
  };

  const { form, onSubmit, isPending } = useAppForm({
    schema: createBookingEventRegistrationMoveSchema,
    defaultValues: {
      event_wave_id: '',
    },
    transform: moveEventRegistrationPayload,
    api_url: buildRegistrationsUrl(eventsUrl, event.id),
    entityId: `${registration.id}/move_to_wave`,
    redirectUrl: '',
    shouldToastFlashMessage: true,
    onSuccess,
  });

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Move participant')}</p>

        <div className="grid gap-2">
          <DetailField label={t('Participant')}>
            <BookingPersonName
              client={registration.client}
              guestInfo={registration.guest_info}
            />
          </DetailField>

          {!!currentWave && (
            <>
              <DetailField label={t('Wave')}>{currentWave.name}</DetailField>

              <DetailField label={t('Date')}>
                {bookingDateTimeRange(
                  currentWave.starts_at,
                  currentWave.ends_at,
                )}
              </DetailField>
            </>
          )}
        </div>

        <Form
          form={form}
          onSubmit={onSubmit}
        >
          <MoveWaveField options={moveOptions(event, registration)} />
        </Form>
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Move')}
        isPending={isPending}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
};
